/**
 * Admin → Performance.
 *
 * The one screen that answers "why does it feel slow *right now*", built from
 * the registry the request middleware already fills. Nothing here is stored,
 * nothing survives a restart, and that is the right trade for a diagnosis tool:
 * a metrics table would mean every request writing a row about itself, which is
 * a write per request added to a game whose problem was writes.
 *
 * Four things are worth looking at, in this order:
 *
 *   1. **Writes queued.** SQLite has one writer and this game serialises them,
 *      so a number here that is not near zero means players are waiting on each
 *      other rather than on any particular query.
 *   2. **Queries per request.** The column that finds an N+1 before it is slow
 *      enough to notice. A page-data endpoint over about forty is worth reading.
 *   3. **p95 by route**, which is what a player actually experiences.
 *   4. **Slow queries**, last because in this codebase they were almost never
 *      the cause — the lock was.
 */

import { api } from '../../lib/api';
import { useAsync } from '../../lib/store';
import { ErrorState, Panel } from '../ui';

interface RouteStat {
  route: string;
  count: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
  avgQueries: number;
  avgDbMs: number;
  errors: number;
}

interface SlowQuery {
  at: number;
  model: string;
  operation: string;
  ms: number;
  route: string | null;
}

interface PerformanceSnapshot {
  config: { slowRequestMs: number; slowQueryMs: number; queryBudget: number; historySize: number };
  overall: {
    windowMinutes: number;
    requests: number;
    p50: number;
    p95: number;
    p99: number;
    max: number;
    errorRate: number;
    avgQueries: number;
    avgDbMs: number;
  };
  routes: RouteStat[];
  slowQueries: SlowQuery[];
  live: {
    requestsInFlight: number;
    queriesInFlight: number;
    peakQueriesInFlight: number;
    writesQueued: number;
    peakWritesQueued: number;
    totalRequests: number;
    totalQueries: number;
    totalErrors: number;
  };
  database: {
    engine: string;
    poolSize: number;
    pragmas: { journalMode: string; synchronous: number; busyTimeout: number } | null;
  };
  actions: { feedsPerMinute: number; warmsPerMinute: number; hatchesPerMinute: number };
}

const ms = (value: number) => `${Math.round(value)}ms`;

export function PerformanceTab() {
  const state = useAsync<PerformanceSnapshot>(() => api.get('/admin/performance'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 320 }} />;

  const { overall, routes, slowQueries, live, database, actions, config } = state.data;

  // WAL is the difference between readers running freely and every reader
  // blocking on every write, so it is called out rather than buried in a list.
  const wal = database.pragmas?.journalMode?.toLowerCase() === 'wal';

  return (
    <div className="stack">
      <div className="row row--between">
        <div className="tiny muted">
          Last {overall.windowMinutes} minutes · {overall.requests} requests · in-memory, cleared on
          restart
        </div>
        <button className="btn btn--sm" onClick={state.reload}>
          Refresh
        </button>
      </div>

      {database.engine === 'sqlite' && !wal && (
        <div className="pxframe" style={{ padding: 'var(--gap-sm)' }}>
          <strong>SQLite is not in WAL mode.</strong>{' '}
          <span className="tiny muted">
            Every read blocks on every write. This is the single biggest thing that makes the game
            stall when several players are active.
          </span>
        </div>
      )}

      <Panel title="◈ Response time" hint={`p95 is what a player notices`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="p50" value={ms(overall.p50)} />
          <Stat label="p95" value={ms(overall.p95)} />
          <Stat label="p99" value={ms(overall.p99)} />
          <Stat label="Slowest" value={ms(overall.max)} />
          <Stat
            label="Error rate"
            value={`${(overall.errorRate * 100).toFixed(1)}%`}
            tone={overall.errorRate > 0 ? 'bad' : 'good'}
          />
        </div>
      </Panel>

      <Panel
        title="⚙ Right now"
        hint="Writes queued is the one to watch — SQLite has a single writer"
      >
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Requests in flight" value={String(live.requestsInFlight)} />
          <Stat
            label="Writes queued"
            value={String(live.writesQueued)}
            hint={`peak ${live.peakWritesQueued}`}
            tone={live.writesQueued > 5 ? 'bad' : undefined}
          />
          <Stat
            label="Queries in flight"
            value={String(live.queriesInFlight)}
            hint={`peak ${live.peakQueriesInFlight} · pool ${database.poolSize}`}
          />
          <Stat label="Queries / request" value={overall.avgQueries.toFixed(1)} />
          <Stat label="DB / request" value={ms(overall.avgDbMs)} />
          <Stat
            label="Database"
            value={database.engine}
            hint={database.pragmas ? `${database.pragmas.journalMode}` : undefined}
            tone={database.engine === 'sqlite' ? (wal ? 'good' : 'bad') : undefined}
          />
        </div>
      </Panel>

      <Panel title="◆ Player actions" hint="Per minute, over the last five">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <Stat label="Feeds / min" value={actions.feedsPerMinute.toFixed(1)} />
          <Stat label="Warms / min" value={actions.warmsPerMinute.toFixed(1)} />
          <Stat label="Hatches / min" value={actions.hatchesPerMinute.toFixed(1)} />
        </div>
      </Panel>

      <Panel
        title="◈ Slowest routes"
        hint={`A query count over ${config.queryBudget} usually means a loop`}
      >
        <div className="pxwell" style={{ overflowX: 'auto', padding: 'var(--gap-sm)' }}>
          <table className="perf-table">
            <thead>
              <tr>
                <th>Route</th>
                <th>n</th>
                <th>p50</th>
                <th>p95</th>
                <th>p99</th>
                <th>max</th>
                <th>queries</th>
                <th>db</th>
                <th>5xx</th>
              </tr>
            </thead>
            <tbody>
              {routes.slice(0, 20).map((route) => (
                <tr key={route.route}>
                  <td className="mono tiny">{route.route}</td>
                  <td>{route.count}</td>
                  <td>{ms(route.p50)}</td>
                  <td>{ms(route.p95)}</td>
                  <td>{ms(route.p99)}</td>
                  <td>{ms(route.max)}</td>
                  <td
                    className="mono"
                    style={{
                      color: route.avgQueries > config.queryBudget ? 'var(--berry-dark)' : undefined,
                    }}
                  >
                    {route.avgQueries.toFixed(1)}
                  </td>
                  <td>{ms(route.avgDbMs)}</td>
                  <td>{route.errors || ''}</td>
                </tr>
              ))}
              {routes.length === 0 && (
                <tr>
                  <td colSpan={9} className="tiny muted">
                    Nothing has been requested in this window yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="◆ Slow queries" hint={`Anything over ${config.slowQueryMs}ms`}>
        <div className="pxwell" style={{ overflowX: 'auto', padding: 'var(--gap-sm)' }}>
          <table className="perf-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Operation</th>
                <th>Took</th>
                <th>Route</th>
              </tr>
            </thead>
            <tbody>
              {slowQueries.slice(0, 25).map((query, index) => (
                <tr key={`${query.at}-${index}`}>
                  <td className="tiny muted">{new Date(query.at).toLocaleTimeString()}</td>
                  <td className="mono tiny">
                    {query.model}.{query.operation}
                  </td>
                  <td>{ms(query.ms)}</td>
                  <td className="mono tiny">{query.route ?? '—'}</td>
                </tr>
              ))}
              {slowQueries.length === 0 && (
                <tr>
                  <td colSpan={4} className="tiny muted">
                    No query has taken longer than {config.slowQueryMs}ms. Nothing to see, which is
                    the point.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

/** Same treatment as Admin's own `Metric`, plus a hint line under the value. */
function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  const color = tone === 'good' ? 'var(--leaf-dark)' : tone === 'bad' ? 'var(--berry-dark)' : undefined;
  return (
    <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono" style={{ color }}>
        {value}
      </div>
      {hint && <div className="tiny muted">{hint}</div>}
    </div>
  );
}
